import QueryKeys from '@src/constants/queryKeys';
import { ControlModeTemplate } from '@src/types/ControlModes';
import { ControlModes as StackControlModes } from '@src/types/Stacks';
import { notify } from '@store/actionCreators/notifications';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { STACK_STRINGS } from '../constants';

// Import types
import { StackModalActionType, UseStackModalReducerProps } from '../types';

// Import utility functions
import { validateStackForm } from '../utils/validateStackForm';
import { handleStackError } from '../utils/errorHandlers';

// Import reducer and initial state
import { initialState, stackModalReducer } from '../context/StackModalReducer';

// Define the hook
const useStackModalReducer = ({
  stackId,
  createStack,
  updateStack,
  onClose,
  initialStackName = '',
  initialControlModes = [],
  isDefault,
}: UseStackModalReducerProps) => {
  const [state, dispatch] = useReducer(stackModalReducer, {
    ...initialState,
    stackName: initialStackName,
    controlModes: initialControlModes,
  });

  const queryClient = useQueryClient();
  const reduxDispatch = useDispatch();

  // Stack form actions
  const setStackName = useCallback((name: string) => {
    dispatch({ type: StackModalActionType.SET_STACK_NAME, payload: name });
  }, []);

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: StackModalActionType.SET_STACK_NAME,
        payload: event.target.value,
      });
    },
    [],
  );

  const showAlert = useCallback((message: string) => {
    dispatch({ type: StackModalActionType.SHOW_ALERT, payload: message });
  }, []);

  const clearAlert = useCallback(() => {
    dispatch({ type: StackModalActionType.CLEAR_ALERT });
  }, []);

  const setValidationErrors = useCallback((errors: Record<string, string>) => {
    dispatch({
      type: StackModalActionType.SET_VALIDATION_ERRORS,
      payload: errors,
    });
  }, []);

  const clearValidationError = useCallback((key: string) => {
    dispatch({
      type: StackModalActionType.CLEAR_VALIDATION_ERROR,
      payload: key,
    });
  }, []);

  const clearControlModeValidationError = useCallback(
    (index: number, field?: string) => {
      dispatch({
        type: StackModalActionType.CLEAR_CONTROL_MODE_VALIDATION_ERROR,
        payload: { index, field },
      });
    },
    [],
  );

  // Control modes actions
  const setControlModes = useCallback((controlModes: StackControlModes[]) => {
    dispatch({
      type: StackModalActionType.SET_CONTROL_MODES,
      payload: controlModes,
    });
  }, []);

  const handleAddControlMode = useCallback(() => {
    dispatch({ type: StackModalActionType.ADD_CONTROL_MODE });
  }, []);

  const handleDuplicateControlMode = useCallback((index: number) => {
    dispatch({
      type: StackModalActionType.DUPLICATE_CONTROL_MODE,
      payload: index,
    });
  }, []);

  const handleRemoveControlMode = useCallback((index: number) => {
    dispatch({
      type: StackModalActionType.REMOVE_CONTROL_MODE,
      payload: index,
    });
  }, []);

  const handleControlModeNameChange = useCallback(
    (index: number, name: string) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_NAME,
        payload: { index, name },
      });
    },
    [],
  );

  const handleControlModeAliasChange = useCallback(
    (index: number, name: string) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_ALIAS,
        payload: { index, name },
      });
    },
    [],
  );

  const handleControlModeEnabledChange = useCallback(
    (index: number, enabled: boolean) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_ENABLED,
        payload: { index, enabled },
      });
    },
    [],
  );

  const handleFieldChange = useCallback(
    (
      controlModeIndex: number,
      sectionIndex: number,
      fieldIndex: number,
      newValue: string | number | boolean | null,
    ) => {
      dispatch({
        type: StackModalActionType.UPDATE_FIELD_VALUE,
        payload: { controlModeIndex, sectionIndex, fieldIndex, newValue },
      });
    },
    [],
  );

  const handleDeleteSection = useCallback(
    (controlModeIndex: number, sectionIndex: number) => {
      dispatch({
        type: StackModalActionType.DELETE_SECTION,
        payload: { controlModeIndex, sectionIndex },
      });
    },
    [],
  );

  // Template selection callback
  const handleTemplatesSelected = useCallback(
    (
      templates: ControlModeTemplate[],
      controlModeIndex: number,
      templateIds?: string[],
    ) => {
      dispatch({
        type: StackModalActionType.TEMPLATES_SELECTED,
        payload: { templates, controlModeIndex, templateIds },
      });
    },
    [],
  );

  // Delete control mode callback
  const handleDeleteControlMode = useCallback((index: number) => {
    dispatch({
      type: StackModalActionType.REMOVE_CONTROL_MODE,
      payload: index,
    });
  }, []);

  // Template picker callbacks
  const handleOpenTemplatePicker = useCallback((index: number) => {
    dispatch({
      type: StackModalActionType.OPEN_TEMPLATE_PICKER,
      payload: index,
    });
  }, []);

  const handleCloseTemplatePicker = useCallback(() => {
    dispatch({ type: StackModalActionType.CLOSE_TEMPLATE_PICKER });
  }, []);

  // Template confirmation dialog callbacks
  const handleOpenTemplateConfirm = useCallback(
    (
      templates: ControlModeTemplate[],
      controlModeIndex: number,
      templateIds?: string[],
    ) => {
      dispatch({
        type: StackModalActionType.OPEN_TEMPLATE_CONFIRM,
        payload: { templates, controlModeIndex, templateIds },
      });
    },
    [],
  );

  const handleCloseTemplateConfirm = useCallback(() => {
    dispatch({ type: StackModalActionType.CLOSE_TEMPLATE_CONFIRM });
  }, []);

  const handleConfirmTemplates = useCallback(() => {
    if (
      state.controlModeIndexForConfirmation !== null &&
      state.templatesForConfirmation &&
      state.templatesForConfirmation.length > 0
    ) {
      handleTemplatesSelected(
        state.templatesForConfirmation,
        state.controlModeIndexForConfirmation,
        state.templateIdsForConfirmation,
      );
      handleCloseTemplateConfirm();
    }
  }, [
    state.controlModeIndexForConfirmation,
    state.templatesForConfirmation,
    state.templateIdsForConfirmation,
    handleTemplatesSelected,
    handleCloseTemplateConfirm,
  ]);

  // Delete confirmation dialog callbacks
  const handleOpenDeleteConfirm = useCallback((index: number) => {
    dispatch({
      type: StackModalActionType.OPEN_DELETE_CONFIRM,
      payload: index,
    });
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    dispatch({ type: StackModalActionType.CLOSE_DELETE_CONFIRM });
  }, []);

  // Form validation
  const validateForm = useCallback(() => {
    const { isValid, errors, nameError } = validateStackForm(
      state.stackName,
      state.controlModes,
    );

    if (nameError) {
      dispatch({
        type: StackModalActionType.SET_NAME_ERROR,
        payload: nameError,
      });
    }

    dispatch({
      type: StackModalActionType.SET_VALIDATION_ERRORS,
      payload: errors,
    });

    return isValid;
  }, [state.stackName, state.controlModes]);

  // Handle control mode name change with validation
  const handleControlModeNameChangeWithValidation = useCallback(
    (index: number, name: string) => {
      handleControlModeNameChange(index, name);
      if (name.trim() && state.validationErrors[`controlMode_${index}_name`]) {
        clearControlModeValidationError(index, 'name');
      }
    },
    [
      handleControlModeNameChange,
      state.validationErrors,
      clearControlModeValidationError,
    ],
  );

  // Handle templates selected with validation
  const handleTemplatesSelectedWithValidation = useCallback(
    (
      templates: ControlModeTemplate[],
      controlModeIndex: number,
      templateIds?: string[],
    ) => {
      handleTemplatesSelected(templates, controlModeIndex, templateIds);
    },
    [handleTemplatesSelected],
  );

  // Save stack
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      showAlert('Please correct the errors before saving');
      return;
    }

    dispatch({ type: StackModalActionType.SET_IS_SAVING, payload: true });

    try {
      // Remove templateIds from each control mode before saving
      const processedControlModes =
        state.controlModes?.map((mode) => {
          const { templateIds, ...modeWithoutTemplateIds } = mode;
          return modeWithoutTemplateIds;
        }) || [];

      const stackData = {
        name: state.stackName,
        controlModes: processedControlModes,
      };

      if (stackId) {
        // Update existing stack
        await updateStack.mutateAsync(stackData);
        // Show success notification
        notify(
          isDefault
            ? STACK_STRINGS.SUCCESS_UPDATE_DEFAULT
            : STACK_STRINGS.SUCCESS_UPDATE,
        )(reduxDispatch);
      } else {
        // Create new stack
        await createStack.mutateAsync(stackData);
        // Show success notification
        notify(
          isDefault
            ? STACK_STRINGS.SUCCESS_CREATE_DEFAULT
            : STACK_STRINGS.SUCCESS_CREATE,
        )(reduxDispatch);
      }

      // Invalidate queries to trigger refetch
      if (isDefault) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys['default-stack']],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.stacks],
        });
      }

      // Close the modal
      onClose();
    } catch (error: unknown) {
      handleStackError(
        error,
        isDefault,
        stackId,
        reduxDispatch,
        onClose,
        queryClient,
      );
    } finally {
      dispatch({ type: StackModalActionType.SET_IS_SAVING, payload: false });
    }
  }, [
    validateForm,
    showAlert,
    state.controlModes,
    state.stackName,
    stackId,
    updateStack,
    createStack,
    queryClient,
    onClose,
    isDefault,
    reduxDispatch,
  ]);

  // Initialize form when stack data is loaded
  const initializeForm = useCallback(
    (stackName: string, controlModes: StackControlModes[]) => {
      dispatch({
        type: StackModalActionType.INITIALIZE_FORM,
        payload: { stackName, controlModes },
      });
    },
    [],
  );

  // Reset form when modal is closed
  const resetForm = useCallback(() => {
    dispatch({ type: StackModalActionType.RESET_FORM });
  }, []);

  return {
    // State
    stackName: state.stackName,
    nameError: state.nameError,
    validationErrors: state.validationErrors,
    alertMessage: state.alertMessage,
    controlModes: state.controlModes,
    isSaving: state.isSaving,
    isTemplatePickerOpen: state.isTemplatePickerOpen,
    currentControlModeIndex: state.currentControlModeIndex,
    isTemplateConfirmOpen: state.isTemplateConfirmOpen,
    templatesForConfirmation: state.templatesForConfirmation,
    controlModeIndexForConfirmation: state.controlModeIndexForConfirmation,
    templateIdsForConfirmation: state.templateIdsForConfirmation,
    isDeleteConfirmOpen: state.isDeleteConfirmOpen,
    controlModeToDelete: state.controlModeToDelete,

    // Actions
    setStackName,
    handleNameChange,
    showAlert,
    clearAlert,
    setValidationErrors,
    clearValidationError,
    clearControlModeValidationError,
    setControlModes,
    handleAddControlMode,
    handleDuplicateControlMode,
    handleRemoveControlMode,
    handleControlModeNameChange,
    handleControlModeEnabledChange,
    handleFieldChange,
    handleDeleteSection,
    validateForm,
    handleControlModeNameChangeWithValidation,
    handleControlModeAliasChange,
    handleSave,
    initializeForm,
    resetForm,

    // Template picker callbacks
    handleTemplatesSelected: handleTemplatesSelectedWithValidation,
    handleOpenTemplatePicker,
    handleCloseTemplatePicker,
    handleOpenTemplateConfirm,
    handleCloseTemplateConfirm,
    handleConfirmTemplates,

    // Delete confirmation callbacks
    handleDeleteControlMode,
    handleOpenDeleteConfirm,
    handleCloseDeleteConfirm,
  };
};

export default useStackModalReducer;
